import { useEffect, useRef, useState } from 'react';
import { hiddenCosts } from '../data';

const MAX_COST = 1000;

const severityConfig = {
  High: { dot: 'bg-accent-500', bar: 'bg-accent-500', label: 'text-accent-700' },
  Medium: { dot: 'bg-primary-500', bar: 'bg-primary-500', label: 'text-primary-700' },
  Low: { dot: 'bg-secondary-500', bar: 'bg-secondary-500', label: 'text-secondary-700' },
} as const;

export default function HiddenCostsGridSection() {
  const [inView, setInView] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleExpand = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section ref={sectionRef} className="w-full bg-background-50 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[640px] mb-12 md:mb-16">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            Six hidden costs eating your profits
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            None of these appear as a line item on your trade ticket. But together, they can cost more than spread and commission combined.
          </p>
        </div>

        {/* Cost audit list */}
        <div className="border-t border-background-200">
          {hiddenCosts.map((cost, index) => {
            const sev = severityConfig[cost.severity as keyof typeof severityConfig];
            const barPercent = Math.round((cost.costMax / MAX_COST) * 100);
            const delay = index * 120;
            const isExpanded = expandedIndex === index;

            return (
              <div
                key={cost.title}
                className="group border-b border-background-200 cursor-pointer"
                onClick={() => toggleExpand(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleExpand(index);
                  }
                }}
              >
                <div className="py-7 md:py-9 flex items-start gap-4 md:gap-7">
                  {/* Number */}
                  <span className="text-[26px] md:text-[32px] font-bold text-foreground-200 shrink-0 w-[40px] md:w-[52px] text-right leading-none pt-0.5">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <i
                          className={`${cost.icon} text-[18px] md:text-[20px] text-foreground-400 w-6 h-6 flex items-center justify-center shrink-0`}
                        />
                        <h3 className="text-[16px] md:text-[18px] font-bold text-foreground-950 truncate">
                          {cost.shortTitle}
                        </h3>
                        <span
                          className={`w-2 h-2 rounded-full ${sev.dot} shrink-0`}
                          title={`Severity: ${cost.severity}`}
                        />
                      </div>

                      <div className="flex items-baseline gap-2 shrink-0">
                        <span className="text-[22px] md:text-[26px] font-bold text-foreground-950">
                          {cost.annualCost.split(' ')[0]}
                        </span>
                        <span className="text-[13px] text-foreground-500 hidden md:inline">
                          {cost.annualCost.split(' ').slice(1).join(' ')}
                        </span>
                      </div>
                    </div>

                    {/* Bar + stats */}
                    <div className="mb-1">
                      <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${sev.bar} transition-[width] duration-[900ms] ease-out`}
                          style={{
                            width: inView ? `${barPercent}%` : '0%',
                            transitionDelay: `${delay}ms`,
                          }}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[13px] text-foreground-500 leading-relaxed">
                        {cost.stats}
                      </p>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </div>
                </div>

                {/* Expanded description */}
                <div
                  className="grid transition-[grid-template-rows] duration-300 ease-out"
                  style={{
                    gridTemplateRows: isExpanded ? '1fr' : '0fr',
                  }}
                >
                  <div className="overflow-hidden">
                    <div className="pb-7 md:pb-9 pl-[56px] md:pl-[76px]">
                      <p className="text-[14px] md:text-[15px] leading-[1.7] text-foreground-600 max-w-[720px]">
                        {cost.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}